import React, { useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { PageLoader, toast } from "@picklebet/ui";
import { parseError } from "@/utilities/errorsParser";
import { PhoneVerification } from "@/features/auth/components/PhoneVerification";
import { useUserToken } from "@/features/auth/hooks/useUserToken";
import { OTP_FLOW } from "@/features/auth/utils/constants";
import { useProfile } from "@/hooks/firestore/user/useProfile";
import { Link } from "@/features/shell/components/shared/Link/Link";
import { useWithdrawWizard } from "@/features/wallet/components/withdraw/hooks/useWithdrawWizard";
import { useAudWithdrawal } from "@/features/wallet/components/withdraw/hooks/useAudWithdrawal";
import { WithdrawContent } from "../../blocks/withdraw-content/WithdrawContent";

import * as styles from "./VerifyMobileStep.module.scss";

const schema = z.object({ phone: z.string() });

export const VerifyMobileStep = () => {
  const { amount } = useWithdrawWizard();
  const { profile } = useProfile();
  const { submit } = useAudWithdrawal();
  const authToken = useUserToken();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const { control, clearErrors } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { phone: profile?.phone ?? "" },
  });

  const handleVerified = async () => {
    setIsSubmitting(true);

    // `verificationRequestId` is deliberately not forwarded yet: the payload
    // accepts it but it is unconfirmed whether the server requires it, so this
    // preserves the existing behaviour. See PKB-13443.
    const result = await submit(amount);

    if (!result.ok) {
      toast({
        variant: "danger",
        title: "Withdrawal Failed",
        description: parseError(result.error),
      });
    }

    setIsSubmitting(false);
  };

  if (isSubmitting || !profile) {
    return <PageLoader text="We're processing your withdrawal..." />;
  }

  return (
    <WithdrawContent>
      <p className={styles.description}>
        Please verify your mobile number to withdraw. Changed your mobile?{" "}
        <Link to="/account/details">Update it here</Link>
      </p>

      <PhoneVerification
        flow={OTP_FLOW.WITHDRAW}
        purpose="MobileNumberVerification"
        channel="USER_MOBILE_SMS"
        authToken={authToken}
        destination={profile.userId}
        control={control}
        clearErrors={clearErrors}
        name="phone"
        onVerified={handleVerified}
        readOnly
        autoSend
      />
    </WithdrawContent>
  );
};
